/* ---------------------------------------------------------------------------
   The standards profile: everything about a rating that belongs to a
   department rather than to the method.

   WHY THIS FILE EXISTS. In the 2009 program these numbers are typed into
   sixteen workbooks — load factors on every LRLFM and POSTLFM sheet, vehicle
   weights on every POSTLFM and POSTASM sheet, material-era tables printed at the
   bottom of every GENERAL and CCASM sheet. A revision to the MLRB is sixteen
   edits, and the one nobody makes is the one somebody is rating a bridge with.
   Correction C6. One profile, versioned, recorded on every rating.

   WHAT IS IN HERE AND WHAT IS NOT. The line is: would another department, using
   this same method, plausibly have a different value? Load factors, yes — they
   are the calibration of the standard. Posting vehicles, obviously. The impact
   formula, no: 15.24/(L+38) is what impact MEANS under this standard, and a
   department using another rule is running another method. That belongs in
   vehicles.ts and stays there.

   THE ERA TABLES ARE A LIBRARY, NOT A LOOKUP THIS ENGINE PERFORMS. This is a
   deliberate departure in shape, though not in numbers. The workbooks reach into
   the table from the calculation sheet, half of the time (correction C8), and
   RCFS reaches only into its first two rows (C12). Here the tables are DATA the
   application offers as a default, and the engine takes the material values it
   was actually given. That is what makes provenance possible: a value filled
   from the table is marked "assumed by era", and a value read off a test
   certificate is marked as measured, and the rating prints which it had. An
   engine that did its own lookups could not tell the reader the difference.

   VERSIONED, AND THE VERSION IS RECORDED. `id` and `version` go onto every
   rating record, so a posting decision from four years ago can be reopened and
   reproduced even after the profile has moved on.
   --------------------------------------------------------------------------- */

import type { VehicleId } from './vehicles.ts';

/* --------------------------- vehicles --------------------------- */

export interface RatingVehicle {
  id: VehicleId;
  /** As the Department writes it on a sign and in a report. */
  name: string;
  /** Gross weight, metric tonnes. The number a posting sign carries. */
  tonnes: number;
}

/* --------------------------- load factors --------------------------- */

/**
 * The two levels every rating is reported at.
 *
 * `inventory` is the load the structure can carry indefinitely; `operating` is
 * the absolute maximum for occasional passage. Both are always reported — never
 * one — because a bridge that fails inventory and passes operating is the normal
 * reason to post rather than to close, and reporting only the governing one
 * throws away the distinction the decision turns on.
 */
export type RatingLevel = 'inventory' | 'operating';

export const RATING_LEVELS: readonly RatingLevel[] = ['inventory', 'operating'];

export interface LoadFactors {
  /** gamma-D, applied to dead load. 1.3 under LFM, 1.0 under ASM. */
  dead: number;
  /** gamma-LL at each level. LFM inventory is 1.3 x 1.67; ASM is 1.0. */
  live: Readonly<Record<RatingLevel, number>>;
}

/* --------------------------- material eras --------------------------- */

/**
 * One row of a material-by-era table.
 *
 * `from` is the first year of construction the row applies to; `null` means
 * "everything earlier", which is how the tables open. `strength` is f'c for
 * concrete and fy for steel; the three allowable stresses are fc or fs at each
 * of the program's three purposes.
 *
 * `note` carries the workbook's `*pc` mark where it has one. Those rows are
 * printed in every GENERAL sheet and referenced by no formula in RCFS, RCDG or
 * PCDG — see correction C12 — and the mark is the only clue as to why. Carried
 * verbatim rather than interpreted.
 */
export interface EraRow {
  label: string;
  from: number | null;
  /** MPa. f'c for concrete, fy for reinforcement and structural steel. */
  strength: number;
  /** MPa. Allowable working stress, by purpose. */
  allowable: { inventory: number; operating: number; posting: number };
  note?: string;
}

export interface EraTables {
  concrete: readonly EraRow[];
  rebar: readonly EraRow[];
  /** Structural steel in bending. SIG only; the RC families have no such table. */
  steelBending: readonly EraRow[];
  /** Structural steel in web shear. SIG only. */
  steelShear: readonly EraRow[];
}

/**
 * The row that applies to a year of construction, or null where the year is
 * unknown.
 *
 * NULL RATHER THAN THE FIRST ROW. A bridge whose year nobody recorded is not a
 * bridge built before 1905. The platform rule is that empty stays empty; a
 * caller that gets null must ask for the material values instead of assuming
 * the oldest and weakest, which would look conservative and be a fabrication.
 *
 * Rows carrying a `note` are skipped: they are not year bands at all — "Grade
 * 50", "Grade 60" — and cannot be selected by year. They are offered to the user
 * by name instead.
 */
export function eraRowFor(table: readonly EraRow[], year: number | null): EraRow | null {
  if (year === null || !Number.isFinite(year)) return null;
  let chosen: EraRow | null = null;
  for (const row of table) {
    if (row.note !== undefined) continue;
    if (row.from === null || year >= row.from) chosen = row;
  }
  return chosen;
}

/* --------------------------- the distribution-factor ruling --------------------------- */

/**
 * Which form of the exterior-girder distribution factor to use.
 *
 * `workbook` is 1 + (S - 1.829)/S, as all sixteen 2009 files compute it.
 * `manual` is (S - 1.829)/S, as the MLRB text prints it. They differ by exactly
 * one wheel line, which at S = 2.4 m is a factor of five on the live load of
 * every exterior girder in the country.
 *
 * A NAMED CHOICE, RECORDED ON THE RATING, AND NOT DEFAULTED SILENTLY. This is an
 * engineering ruling the Department owes us (correction C11); until it arrives,
 * the profile carries the workbook form so the reference values reproduce, and
 * every rating says which form produced it. Nothing here will quietly pick one.
 */
export type ExteriorDfRule = 'workbook' | 'manual';

/* --------------------------- the profile --------------------------- */

export interface StandardsProfile {
  /** Stable id, recorded on every rating. */
  id: string;
  /** Bumped whenever any number below changes. Recorded on every rating. */
  version: string;
  /** For the report cover. */
  name: string;
  /** The document this profile is a transcription of. */
  authority: string;

  /** The vehicle a rating factor is expressed against. MS18 under CBPLR. */
  ratingVehicle: RatingVehicle;
  /** The trucks a posting sign can name. */
  postingVehicles: readonly RatingVehicle[];

  factors: {
    lfm: LoadFactors;
    asm: LoadFactors;
  };

  /**
   * The nominal capacity factor on ultimate moment, `Uf`.
   *
   * 0.9 throughout CBPLR. Damage reductions multiply this rather than replace
   * it — see sections.ts.
   */
  capacityFactor: number;

  /** Nominal shear capacity factor, phi-c. 0.85 throughout. */
  shearCapacityFactor: number;

  /**
   * Modular ratio n = Es/Ec for working-stress calculations.
   *
   * Typed as 12 in every CCASM sheet rather than computed from f'c, which is the
   * 1960s convention the method was written in. Kept as a profile value because
   * it is a calibration choice, not a fact.
   */
  modularRatio: number;

  /**
   * Allowable stresses for structural steel, as fractions of fy.
   *
   * NOT the era table, and this contradicts the study notes, which read SIG's
   * allowables as coming from one. The reference workbook is a 1970 bridge with
   * fy = 482 MPa and allowables typed as 265 / 361.5 bending and 160.7 / 216.9
   * shear. Those are 0.55 fy, 0.75 fy, 0.333 fy and 0.45 fy — exact to four
   * significant figures on all four values, which is not coincidence. The era
   * table is the fallback for steel whose grade is unknown, which is what its
   * bands (Prior to 1905, 1905 to 1936) are for.
   *
   * TO BE CONFIRMED AGAINST THE MANUAL before the SIG family is released. The
   * numbers reproduce either way; what is uncertain is which rule to apply to a
   * bridge whose fy IS known and is not 482.
   */
  steelAllowable: {
    bending: Readonly<Record<RatingLevel, number>>;
    shear: Readonly<Record<RatingLevel, number>>;
  };

  eras: EraTables;

  exteriorDf: ExteriorDfRule;

  /**
   * The overall condition ratings at which a load rating is required.
   *
   * `MENU!BCIR` gates the whole program on this: Good and Fair need no rating,
   * Poor and Bad do. Held as the department's own condition codes, because the
   * scale is departmental — DPWH grades G/F/P/B, DoR grades 0 to 4 on C/E/U —
   * and the platform stores a condition with its own scale and normalises only
   * for display.
   */
  ratingRequiredAt: readonly string[];
}

/* --------------------------- DPWH / CBPLR 2009 --------------------------- */

const RC_CONCRETE: readonly EraRow[] = [
  {
    label: 'Prior to 1959',
    from: null,
    strength: 17.2,
    allowable: { inventory: 6.9, operating: 10.3, posting: 6.9 },
  },
  {
    label: 'After 1959',
    from: 1959,
    strength: 20.7,
    allowable: { inventory: 8.3, operating: 13.1, posting: 8.3 },
  },
  {
    label: '1977 to 1981',
    from: 1977,
    strength: 27.6,
    allowable: { inventory: 11, operating: 16.5, posting: 11 },
    note: '*pc',
  },
  {
    label: 'After 1981',
    from: 1981,
    strength: 31,
    allowable: { inventory: 12.4, operating: 18.6, posting: 12.4 },
    note: '*pc',
  },
];

const RC_REBAR: readonly EraRow[] = [
  {
    label: 'Prior to 1954',
    from: null,
    strength: 227.5,
    allowable: { inventory: 124.1, operating: 172.4, posting: 124.1 },
  },
  {
    label: 'After 1954',
    from: 1954,
    strength: 275.8,
    allowable: { inventory: 137.9, operating: 193.1, posting: 137.9 },
  },
  {
    label: 'Grade 50',
    from: null,
    strength: 344.7,
    allowable: { inventory: 137.9, operating: 224.1, posting: 137.9 },
    note: '*pc',
  },
  {
    label: 'Grade 60',
    from: null,
    strength: 413.7,
    allowable: { inventory: 165.5, operating: 248.2, posting: 165.5 },
    note: '*pc',
  },
];

/* SIG's own tables. Four real year bands each, unlike the RC pair — which is
   the clearest evidence that RCFS's two-way lookup (C12) is a truncation rather
   than the intended design. */
const STEEL_BENDING: readonly EraRow[] = [
  {
    label: 'Prior to 1905',
    from: null,
    strength: 179.3,
    allowable: { inventory: 96.5, operating: 134.4, posting: 96.5 },
  },
  {
    label: '1905 to 1936',
    from: 1905,
    strength: 206.8,
    allowable: { inventory: 110.3, operating: 155.1, posting: 110.3 },
  },
  {
    label: '1936 to 1963',
    from: 1936,
    strength: 227.5,
    allowable: { inventory: 124.1, operating: 168.9, posting: 124.1 },
  },
  {
    label: 'After 1963',
    from: 1963,
    strength: 248.2,
    allowable: { inventory: 137.9, operating: 186.2, posting: 137.9 },
  },
];

const STEEL_SHEAR: readonly EraRow[] = [
  {
    label: 'Prior to 1905',
    from: null,
    strength: 179.3,
    allowable: { inventory: 58.6, operating: 79.3, posting: 58.6 },
  },
  {
    label: '1905 to 1936',
    from: 1905,
    strength: 206.8,
    allowable: { inventory: 65.5, operating: 93.1, posting: 65.5 },
  },
  {
    label: '1936 to 1963',
    from: 1936,
    strength: 227.5,
    allowable: { inventory: 75.8, operating: 103.4, posting: 75.8 },
  },
  {
    label: 'After 1963',
    from: 1963,
    strength: 248.2,
    allowable: { inventory: 82.7, operating: 110.3, posting: 82.7 },
  },
];

/**
 * DPWH, as the 2009 program computes it.
 *
 * Every number here is transcribed from the workbooks and pinned by a test
 * against the recorded values. Change one and the suite says which reference
 * value it moved.
 */
export const DPWH_CBPLR: StandardsProfile = {
  id: 'dpwh-cbplr',
  version: '2009.1',
  name: 'DPWH — Computer-Based Program on Load Rating',
  authority: 'Manual for Load Rating of Bridges (MLRB), 2nd Edition, March 2009',

  ratingVehicle: { id: 'MS18', name: 'MS18 (HS20-44)', tonnes: 33 },
  postingVehicles: [
    { id: 'Type1-1', name: 'Type 1-1', tonnes: 17 },
    { id: 'Type1-2', name: 'Type 1-2', tonnes: 27 },
    { id: 'Type12-2', name: 'Type 12-2', tonnes: 38 },
  ],

  factors: {
    lfm: {
      dead: 1.3,
      /* 2.17 is 1.3 x 1.67 rounded to two decimals, which is how the sheet
         writes it: `ROUND(H5*1.67,2)`. Stored as the rounded value rather than
         the product, because the sheet's rounded value is what every reference
         rating factor was divided by. */
      live: { inventory: 2.17, operating: 1.3 },
    },
    asm: {
      dead: 1,
      live: { inventory: 1, operating: 1 },
    },
  },

  capacityFactor: 0.9,
  shearCapacityFactor: 0.85,
  modularRatio: 12,

  steelAllowable: {
    bending: { inventory: 0.55, operating: 0.75 },
    shear: { inventory: 1 / 3, operating: 0.45 },
  },

  eras: {
    concrete: RC_CONCRETE,
    rebar: RC_REBAR,
    steelBending: STEEL_BENDING,
    steelShear: STEEL_SHEAR,
  },

  exteriorDf: 'workbook',

  ratingRequiredAt: ['P', 'B'],
};

/** Every profile this build knows. One today; the shape is what matters. */
export const PROFILES: readonly StandardsProfile[] = [DPWH_CBPLR];

export function profileById(id: string): StandardsProfile | null {
  return PROFILES.find((p) => p.id === id) ?? null;
}

/**
 * Whether a load rating is called for at this overall condition.
 *
 * `null` for a structure nobody has graded — which is not "no", and the caller
 * must say so rather than reporting that no rating is needed.
 */
export function ratingRequired(profile: StandardsProfile, condition: string | null): boolean | null {
  if (condition === null || condition.trim() === '') return null;
  return profile.ratingRequiredAt.includes(condition.trim().toUpperCase());
}
